import { Ref } from "vue";

import type { PagesForestNode } from "@/page";

export interface PageOrder {
  [parentId: string]: string[];
}

/**
 * Derive what the pages lists render: the forest with the device-local
 * sibling order applied, as *copies* of the forest's nodes. The copies are
 * deliberate: drags mutate the rendered arrays in place (vuedraggable
 * splices the dragged node between lists optimistically), and those
 * mutations must stay confined to the disposable displayed forest — the
 * canonical one is only updated once the move is persisted, and the next
 * derivation re-lands the display on it.
 */
export function getHierarchy(
  pages: PagesForestNode[],
  hierarchy: Ref<PagesForestNode[]>,
  pageOrder: Ref<PageOrder>
): PagesForestNode[] {
  // Every id in the forest, for the saved-order self-heal below: an order
  // entry is only pruned when its node is gone from the whole forest —
  // an entry whose node currently sits under *another* parent is an
  // in-flight reparent (the drop recorded the position, the forest
  // catches up when persistence lands and its ticks re-derive); scrubbing
  // it would lose the drop position, re-sorting the page to the end.
  const allIds = new Set<string>();
  const collect = (nodes: PagesForestNode[]): void => {
    for (const node of nodes) {
      allIds.add(node.id);
      collect(node.children);
    }
  };
  collect(pages);

  const sortPages = (
    pages: PagesForestNode[],
    parentId: string
  ): PagesForestNode[] => {
    const orderForParent = pageOrder.value[parentId] || [];

    const sortedPages = [...pages].sort((a, b) => {
      const indexA = orderForParent.indexOf(a.id);
      const indexB = orderForParent.indexOf(b.id);

      // If both pages are in the saved order, sort them based on their positions
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only page A is in the saved order, it should come first
      if (indexA !== -1) return -1;
      // If only page B is in the saved order, it should come first
      if (indexB !== -1) return 1;
      // If neither page is in the saved order, maintain their current relative position
      return 0;
    });

    const copies = sortedPages.map((page) => ({
      ...page,
      children:
        page.children.length > 0 ? sortPages(page.children, page.id) : [],
    }));

    // Self-heal the saved order additively: drop the entries whose node
    // left the forest entirely, append the children it doesn't know yet
    // (in their displayed order) — and keep everything else, in-flight
    // reparents included.
    const kept = orderForParent.filter((id) => allIds.has(id));
    const missing = copies
      .map((page) => page.id)
      .filter((id) => !kept.includes(id));
    const newOrder = [...kept, ...missing];

    pageOrder.value = {
      ...pageOrder.value,
      [parentId]: newOrder,
    };

    return copies;
  };

  hierarchy.value = sortPages(pages, "root");
  return hierarchy.value;
}

/**
 * Record a drag result in the device-local sibling order: `id` now sits at
 * `newIndex` under `parentId` (null = the root). The page is removed from
 * every parent's order first — not just its previous one — so a stale entry
 * (e.g. from an interrupted drag) cannot linger and re-sort the node
 * surprisingly later. Returns a new object; the caller owns the writable
 * order ref.
 */
export function recordPageOrder(
  order: PageOrder,
  id: string,
  parentId: string | null,
  newIndex: number
): PageOrder {
  const next = Object.fromEntries(
    Object.entries(order).map(([parent, ids]) => [
      parent,
      ids.filter((other) => other !== id),
    ])
  );
  const siblings = next[parentId ?? "root"] ?? [];
  siblings.splice(newIndex, 0, id);
  next[parentId ?? "root"] = siblings;
  return next;
}
